import { JSX, useState } from 'react';
import { useFormik } from 'formik';
import { getOnboardOrganizationSchema } from './schema';
import OnboardOrganizationStepOne from '../../views/OnboardOrganizationStepOne';
import OnboardOrganizationStepTwo from '../../views/OnboardOrganizationStepTwo';
import OnboardOrganizationStepThree from '../../views/OnboardOrganizationStepThree';
import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';
import moment from 'moment';
import { IndustryType, CompanySize } from '@EHSNavigator/backend';
import { useNavigate } from 'react-router';

const INITIAL_STATE = {
  // Organization details
  companyName: '',
  industryType: '',
  industrySize: '',
  headquarterAddress: '',
  organizationContact: '',
  isPaid: false,
  licenseCount: '',
  amount: '',
  // Owner details
  fullName: '',
  email: '',
  contact: '',
  dob: '',
  password: '',
  confirmPassword: '',
};

export default function OnboardOrganizationPage() {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const { AuthApi, OnboardOrganizationApi } = useApiClients();
  const navigate = useNavigate();

  const onboardOrganizationHandler = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getOnboardOrganizationSchema(currentStep),
    onSubmit: async (values) => {
      console.log(values, 'valuessss');
      if (currentStep === 3) {
        try {
          const response = await OnboardOrganizationApi.onboardOrganizationControllerCreate({
            createOnboardOrganizationDto: {
              organizationName: values.companyName,
              organizationAddress: values.headquarterAddress,
              industryType: values.industryType as IndustryType,
              size: values.industrySize as CompanySize,
              organizationContact: values.organizationContact ? String(values.organizationContact) : undefined,
              amount: values.amount ? Number(values.amount) : undefined,
              fullName: values.fullName,
              email: values.email,
              contact: values.contact ? String(values.contact) : undefined,
              licenseCount: values.licenseCount ? Number(values.licenseCount) : 0,
              isPaid: values.isPaid,
              dob: values.dob && moment(values.dob).isValid()
                ? moment(values.dob).format('DD/MM/YYYY')
                : undefined,
            },
          });

          toast.success(response?.data?.message || 'Organization onboarding form submitted successfully!');

          navigate('/admin/customers');
          setCurrentStep(1);
          onboardOrganizationHandler?.resetForm();
        } catch (error: any) {
          const errorMessage = error?.response?.data?.message;
          if (Array.isArray(errorMessage)) {
            errorMessage.forEach((msg: string) => toast.error(msg));
          } else {
            toast.error(errorMessage || 'An unexpected error occurred.');
          }
        }
      }
    },
  });

  const isOnboardOrganizationLoading = onboardOrganizationHandler.isSubmitting;

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      window.scrollTo(0, 0);
      setCurrentStep(steps);
    } else {
      const errors = await onboardOrganizationHandler.validateForm();

      if (Object.keys(errors).length === 0) {
        if (currentStep === 1 && steps === 2) {
          try {
            const res = await AuthApi.authControllerCheckEmailExist({
              checkEmailExistDTO: {
                email: onboardOrganizationHandler?.values?.email,
              }
            });
            if (res?.data.exist === false) {
              setCurrentStep(steps);
              window.scrollTo(0, 0);
            } else {
              onboardOrganizationHandler?.setFieldError(
                'email',
                'Email already exists',
              );
            }
          } catch (error: any) {
            const errorMessage = error?.response?.data?.message;
            if (Array.isArray(errorMessage)) {
              errorMessage.forEach((msg: string) => toast.error(msg));
            } else {
              toast.error(errorMessage || 'An unexpected error occurred.');
            }
          }
        } else {
          setCurrentStep(steps);
          window.scrollTo(0, 0);
        }
      } else {
        const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
          acc[key] = true;
          return acc;
        }, {});
        onboardOrganizationHandler.setTouched(touchedFields);
      }
    }
  };

  const steps: Record<number, JSX.Element> = {
    1: (
      <OnboardOrganizationStepOne
        formHandler={onboardOrganizationHandler}
        handleSteps={handleNextStep}
      />
    ),
    2: (
      <OnboardOrganizationStepTwo
        formHandler={onboardOrganizationHandler}
        handleSteps={handleNextStep}
      />
    ),
    3: (
      <OnboardOrganizationStepThree
        formHandler={onboardOrganizationHandler}
        handleSteps={handleNextStep}
        isLoading={isOnboardOrganizationLoading}
      />
    ),
  };

  return (
    <div className="relative flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <form
        action="#"
        className="flex flex-1 flex-col"
        onSubmit={onboardOrganizationHandler?.handleSubmit}
      >
        {steps[currentStep]}
      </form>
    </div>
  );
}
